"use strict";

import BaseModel from "@/shared/lib/client-sdk/base-model";
import ApiRequest from "@/shared/lib/client-sdk/api-request";
import $ from "jquery";

class SavedSearch extends BaseModel {
  constructor(idOrAttributes, onRefreshCallback = null) {
    if ($.type(idOrAttributes) == "string") {
      const normalized = SavedSearch.normalizeUuid(idOrAttributes);
      if (normalized) {
        super(normalized, onRefreshCallback);
        return;
      }
    }
    super(idOrAttributes, onRefreshCallback);
  }

  /**
   * Returns 32-char lowercase hex for API paths / refresh, or null.
   * Accepts dashed GUIDs from .NET JSON.
   */
  static normalizeUuid(value) {
    if (value == null) return null;
    const compact = String(value).trim().replace(/-/g, "");
    if (compact.length === 32 && /^[a-fA-F0-9]{32}$/.test(compact)) {
      return compact.toLowerCase();
    }
    return null;
  }

  // The API sends the numeric row Id as `id`, but BaseModel only PATCHes when
  // `id` is the 32-char uuid; otherwise every update() becomes a create.
  _loadAttributes(attributes) {
    super._loadAttributes(attributes);

    const uuid = SavedSearch.normalizeUuid(this.uuid);
    if (uuid) this.set("id", uuid);
  }

  static getModelClassName() {
    return "SavedSearch";
  }

  static getApiPathForSearch() {
    return (
      window.amplifyRuntimeConfig.get("VUE_APP_API_BASE_URL") +
      "/public/saved-searches"
    );
  }

  getApiPathForCreate() {
    return SavedSearch.getApiPathForSearch();
  }

  getApiPathForRefreshUpdateAndDelete() {
    const key =
      SavedSearch.normalizeUuid(this.uuid) ||
      SavedSearch.normalizeUuid(this.id) ||
      String(this.uuid || this.id || "");
    return SavedSearch.getApiPathForSearch() + "/" + key;
  }

  get searchResultTitle() {
    return this.displayName;
  }

  get searchResultSubtitle() {
    return [this.entityType, this.pagePath || ""];
  }

  get searchResultCreatedAt() {
    return this.createdAt;
  }

  get searchResultStatus() {
    return null;
  }

  getAdminPath() {
    return "/saved-searches";
  }

  get displayName() {
    return SavedSearch.getEntityPrefix(this.entityType) + ": " + this.name;
  }

  static getEntityPrefix(entityType) {
    if (!entityType) return "NA";

    // Explicit overrides where the auto rule would collide or abbreviate poorly.
    const overrides = {
      Order: "OR",
      Plan: "PL",
      Account: "AC",
      User: "US",
      Payment: "PM",
      Participant: "PA",
    };
    if (overrides[entityType]) return overrides[entityType];

    // Auto: join all capital letters (OrderLineDocument -> OLD, PaymentInstruction -> PI).
    // Single-capital names fall back to the first two characters uppercased.
    const capitals = entityType.match(/[A-Z]/g);
    if (capitals && capitals.length >= 2) return capitals.join("");
    return entityType.slice(0, 2).toUpperCase();
  }

  static fetchSavedSearches(callback, params = {}) {
    let requestUrl = SavedSearch.getApiPathForSearch();
    const query = [];

    if (params.entityType) {
      query.push("entityType=" + encodeURIComponent(params.entityType));
    }
    if (params.limit) {
      query.push("limit=" + encodeURIComponent(params.limit));
    }

    if (query.length > 0) {
      requestUrl += "?" + query.join("&");
    }

    ApiRequest.send("GET", requestUrl).onComplete((response) => {
      if (!$.isFunction(callback)) return;

      if (response.successful && $.isArray(response.result)) {
        callback(response.result.map((item) => new SavedSearch(item)), null);
      } else {
        callback([], response);
      }
    });
  }

  touch(callback) {
    const requestUrl = this.getApiPathForRefreshUpdateAndDelete() + "/touch";
    ApiRequest.send("PATCH", requestUrl).onComplete((response) => {
      if (response.successful && response.result) {
        this._loadAttributes(response.result);
        this._makeClean();
      }

      if ($.isFunction(callback)) callback(response);
    });
  }
}

export default SavedSearch;
